<script setup lang="ts">
import { ElRow, ElCol } from "element-plus";
import { useI18n } from "vue-i18n";
import CoreLink from "@/components/common/CoreLink/CoreLink.vue";
import { config } from "@/config/envConfig";
import LoginForm from "@/forms/LoginForm.vue";
import { LatestBlogPost, SecurityCertifications } from "@/widgets/";
import SetPasswordForm from "@/forms/SetPasswordForm.vue";
import { useRoute } from "vue-router";
import { computed, watch, ref } from "vue";
import { ADMIN_CREATE_USER, FORGOT_PASSWORD } from "@/stores/constants";
import useAuthStore from "@/stores/auth";

const { t } = useI18n();
const route = useRoute();
const authStore = useAuthStore();

const PRODUCT_NAME = config.APP_PRODUCT_NAME;
const SHOW_CREATE_ACCOUNT_BUTTON = config.SHOW_CREATE_ACCOUNT_BUTTON;
const homepageUrl = t("urls.marketing.root");
const { trigger, code } = route.query;
const passwordSuccess = ref(false);

const isSettingPassword = computed(() => {
  if (code && !passwordSuccess.value) {
    return ADMIN_CREATE_USER === trigger || FORGOT_PASSWORD === trigger;
  }
  return false;
});

watch(
  isSettingPassword,
  async (newValue) => {
    if (newValue && authStore.authUser) {
      await authStore.logout();
    }
  },
  { immediate: true }
);

const setPasswordSuccess = () => {
  passwordSuccess.value = true;
};
</script>

<template>
  <div class="container">
    <div
      v-if="isSettingPassword"
      class="set-password-container"
    >
      <SetPasswordForm @on-success="setPasswordSuccess" />
    </div>
    <el-row
      v-else
      class="content"
    >
      <el-col
        class="content_news"
        :lg="11"
        :md="11"
      >
        <div class="news-tiles"><LatestBlogPost /></div>
      </el-col>
      <el-col
        class="content_login"
        :lg="13"
        :md="13"
      >
        <el-row class="logo-header">
          <CoreLink
            :href="homepageUrl"
            class="logo"
            :underline="false"
          >
            <img
              :alt="PRODUCT_NAME"
              src="@theme/branding/brand_primary_logo.svg"
              class="brand-logo"
            />
          </CoreLink>
          <CoreLink
            v-if="SHOW_CREATE_ACCOUNT_BUTTON"
            :route="{ name: 'register' }"
            :underline="false"
          >
            {{ $t("forms.login.createAccount") }}
          </CoreLink>
        </el-row>
        <el-row class="login-form">
          <LoginForm />
        </el-row>
        <el-row>
          <SecurityCertifications />
        </el-row>
      </el-col>
    </el-row>
  </div>
</template>

<style lang="scss" scoped>
.set-password-container {
  display: flex;
  min-height: 100vh;
  min-width: 100vw;
  justify-content: center;
  align-items: center;
}

.content {
  flex-direction: column-reverse;
  height: 100%;
  justify-content: center;
  min-height: 100vh;
  background: var(--so-bg-color);

  @media screen and (min-width: ($breakpoint-md + 1)) {
    justify-content: left;
    flex-direction: row;
    height: 100%;
  }
}

.content_news,
.content_login {
  padding-left: 30px;
  padding-right: 30px;

  @media screen and (min-width: ($breakpoint-md + 1)) {
    padding-top: 125px;
  }
}

.content_news {
  display: none;
  flex-grow: 1;
  background-color: var(--so-bg-color-login);

  @media screen and (min-width: ($breakpoint-md)) {
    display: block;
  }

  .news-tiles {
    justify-content: center;
    max-width: 420px;
    margin-left: auto;
    margin-right: 70px;
    width: 100%;
  }
}

.content_login {
  justify-content: center;
  max-width: 450px;
  margin: 0 auto;
  width: 100%;

  @media all and (min-width: $breakpoint-lg) {
    margin-right: auto;
    margin-left: 70px;
  }
}

.logo-header {
  margin-bottom: 25px;
  text-align: center;

  a {
    color: var(--color-link);
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: var(--font-normal);
    line-height: 34px;

    &.logo {
      flex-grow: 1;
      justify-content: flex-start;
      width: 100%;
      margin-bottom: 25px;

      @media screen and (min-width: $breakpoint-xs) {
        margin-bottom: 0;
        width: auto;
      }
    }
  }

  @media screen and (min-width: $breakpoint-xs) {
    margin-bottom: 55px;
  }
}

.brand-logo {
  height: 40px;
}
</style>
